import { identifyLogRocketUser } from "./logRocket";
import { API_BASE } from "../config/constants";

export const USER_DATA_KEY = "userData";
export const SESSION_UPDATED_EVENT = "app:session-updated";

const canUseStorage = () =>
  typeof window !== "undefined" && Boolean(window?.localStorage);

const parseJson = (raw, fallback = null) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const dispatchSessionUpdated = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(SESSION_UPDATED_EVENT));
};

const normalizeRestaurantId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isFalsyWebAccess = (value) =>
  value === 0 || value === "0" || value === false;

const normalizeAccountPayload = (account) => {
  if (!account || typeof account !== "object") return null;

  const id = normalizeRestaurantId(
    account?.id ?? account?.restaurantid ?? account?.restaurant_id
  );
  if (!id) return null;

  return {
    ...account,
    id,
    role: normalizeRole(account?.role || "owner") || "owner",
  };
};

export const hasLinkedRestaurantWebAccess = (account) => {
  if (!account || typeof account !== "object") return false;
  const rawAccess =
    account?.isWebAllowed ??
    account?.isweballowed ??
    account?.web_allowed ??
    account?.webAllowed;

  if (rawAccess === null || rawAccess === undefined || rawAccess === "") {
    return true;
  }

  return !isFalsyWebAccess(rawAccess);
};

export const getLinkedRestaurantWebAccessError = () =>
  "This linked restaurant does not have web access. Please purchase the Professional plan or contact support.";

const dedupeAccounts = (accounts = []) => {
  const next = [];
  const seen = new Set();

  (Array.isArray(accounts) ? accounts : []).forEach((entry) => {
    const normalized = normalizeAccountPayload(entry);
    if (!normalized || seen.has(normalized.id)) return;
    seen.add(normalized.id);
    next.push(normalized);
  });

  return next;
};

const buildStandaloneContext = (user) => {
  const normalizedUser = normalizeAccountPayload(user);
  if (!normalizedUser) return null;

  return {
    primary_restaurant_id: normalizedUser.id,
    active_restaurant_id: normalizedUser.id,
    accounts: [normalizedUser],
  };
};

export const getStoredSessionSnapshot = () =>
  canUseStorage()
    ? parseJson(window.localStorage.getItem(USER_DATA_KEY), {})
    : {};

export const normalizeAccountLinkContext = (context, fallbackUser = null) => {
  const fallbackAccount = normalizeAccountPayload(fallbackUser);
  const accounts = dedupeAccounts(context?.accounts || []);

  if (!accounts.length && fallbackAccount) {
    accounts.push(fallbackAccount);
  }

  const requestedPrimaryId = normalizeRestaurantId(context?.primary_restaurant_id);
  const requestedActiveId = normalizeRestaurantId(context?.active_restaurant_id);

  if (
    requestedPrimaryId &&
    fallbackAccount &&
    fallbackAccount.id === requestedPrimaryId &&
    !accounts.some((account) => account.id === requestedPrimaryId)
  ) {
    accounts.unshift(fallbackAccount);
  }

  if (!accounts.length) return null;

  const primaryAccount =
    accounts.find((account) => account.id === requestedPrimaryId) ||
    accounts.find((account) => account.id === fallbackAccount?.id) ||
    accounts[0];

  const activeAccount =
    accounts.find((account) => account.id === requestedActiveId) ||
    accounts.find((account) => account.id === fallbackAccount?.id) ||
    primaryAccount;

  return {
    primary_restaurant_id: primaryAccount.id,
    active_restaurant_id: activeAccount.id,
    accounts,
  };
};

export const getAccountLinkContext = (session = getStoredSessionSnapshot()) =>
  normalizeAccountLinkContext(session?.account_link_context, session?.data || null);

export const getLinkedRestaurantAccounts = (session = getStoredSessionSnapshot()) =>
  getAccountLinkContext(session)?.accounts || [];

export const getActiveLinkedRestaurant = (session = getStoredSessionSnapshot()) => {
  const context = getAccountLinkContext(session);
  if (!context) return normalizeAccountPayload(session?.data || null);
  return (
    context.accounts.find(
      (account) => account.id === context.active_restaurant_id
    ) || normalizeAccountPayload(session?.data || null)
  );
};

export const isOwnerSession = (session = getStoredSessionSnapshot()) =>
  normalizeRole(session?.data?.role) === "owner";

export const canManageLinkedRestaurants = (session = getStoredSessionSnapshot()) => {
  if (!isOwnerSession(session)) return false;
  const context = getAccountLinkContext(session);
  const currentId = normalizeRestaurantId(session?.data?.id);
  return Boolean(
    context &&
      currentId &&
      currentId === normalizeRestaurantId(context.primary_restaurant_id)
  );
};

export const isPrimaryLinkedRestaurantSession = (
  session = getStoredSessionSnapshot()
) => {
  if (!isOwnerSession(session)) return false;
  const context = getAccountLinkContext(session);
  const currentId = normalizeRestaurantId(session?.data?.id);
  const primaryId = normalizeRestaurantId(context?.primary_restaurant_id);
  const accounts = Array.isArray(context?.accounts) ? context.accounts : [];

  return Boolean(
    currentId &&
      primaryId &&
      currentId === primaryId &&
      accounts.length > 1
  );
};

export const writeStoredSession = (
  session,
  { preserveAccountLinkContext = true, syncLogRocket = true } = {}
) => {
  if (!canUseStorage()) return session || {};

  const previousSession = getStoredSessionSnapshot();
  const nextSession =
    session && typeof session === "object" ? { ...session } : {};

  const hasIncomingContext = Object.prototype.hasOwnProperty.call(
    nextSession,
    "account_link_context"
  );

  if (
    preserveAccountLinkContext &&
    !hasIncomingContext &&
    previousSession?.account_link_context
  ) {
    nextSession.account_link_context = previousSession.account_link_context;
  }

  if (nextSession.account_link_context === null) {
    delete nextSession.account_link_context;
  }

  const normalizedContext = getAccountLinkContext(nextSession);
  if (normalizedContext) {
    nextSession.account_link_context = normalizedContext;
    const activeAccount =
      normalizedContext.accounts.find(
        (account) => account.id === normalizedContext.active_restaurant_id
      ) || normalizedContext.accounts[0];
    if (activeAccount) {
      nextSession.data = {
        ...(nextSession?.data || {}),
        ...activeAccount,
      };
    }
  }

  window.localStorage.setItem(USER_DATA_KEY, JSON.stringify(nextSession));
  if (syncLogRocket) {
    identifyLogRocketUser(nextSession);
  }
  dispatchSessionUpdated();
  return nextSession;
};

export const clearAccountLinkContext = () => {
  const session = getStoredSessionSnapshot();
  if (!session || !Object.keys(session).length) return {};
  const nextSession = { ...session };
  delete nextSession.account_link_context;
  return writeStoredSession(nextSession, {
    preserveAccountLinkContext: false,
  });
};

export const setAccountLinkContext = (context, activeRestaurantId = null) => {
  const session = getStoredSessionSnapshot();
  const fallbackUser = session?.data || null;
  let normalized = normalizeAccountLinkContext(context, fallbackUser);

  if (!normalized) {
    normalized = buildStandaloneContext(fallbackUser);
  }

  if (normalized && activeRestaurantId) {
    const requestedId = normalizeRestaurantId(activeRestaurantId);
    if (
      requestedId &&
      normalized.accounts.some((account) => account.id === requestedId)
    ) {
      normalized = {
        ...normalized,
        active_restaurant_id: requestedId,
      };
    }
  }

  return writeStoredSession(
    {
      ...(session || {}),
      account_link_context: normalized,
    },
    { preserveAccountLinkContext: false }
  );
};

export const applyAccountLinkContext = (context) => {
  const session = getStoredSessionSnapshot();
  const existingContext = getAccountLinkContext(session);
  const requestedActiveId = normalizeRestaurantId(
    existingContext?.active_restaurant_id
  );
  const normalized = normalizeAccountLinkContext(context, session?.data || null);

  if (
    normalized &&
    requestedActiveId &&
    normalized.accounts.some((account) => account.id === requestedActiveId)
  ) {
    return setAccountLinkContext(
      {
        ...normalized,
        active_restaurant_id: requestedActiveId,
      },
      requestedActiveId
    );
  }

  return setAccountLinkContext(normalized);
};

export const switchLinkedRestaurant = (restaurantId) => {
  const session = getStoredSessionSnapshot();
  const context = getAccountLinkContext(session);
  if (!context) {
    throw new Error("No linked restaurant context found.");
  }

  const requestedId = normalizeRestaurantId(restaurantId);
  const targetAccount = context.accounts.find(
    (account) => account.id === requestedId
  );

  if (!targetAccount) {
    throw new Error("Selected restaurant is not available in this session.");
  }

  if (!hasLinkedRestaurantWebAccess(targetAccount)) {
    throw new Error(getLinkedRestaurantWebAccessError());
  }

  return writeStoredSession(
    {
      ...(session || {}),
      data: {
        ...(session?.data || {}),
        ...targetAccount,
      },
      account_link_context: {
        ...context,
        active_restaurant_id: targetAccount.id,
      },
    },
    { preserveAccountLinkContext: false }
  );
};

export const hydrateAccountLinkContext = async ({ force = false } = {}) => {
  const session = getStoredSessionSnapshot();
  const user = session?.data || null;
  if (!normalizeAccountPayload(user) || normalizeRole(user?.role) !== "owner") {
    return null;
  }

  const existingContext = getAccountLinkContext(session);
  if (!force && existingContext?.accounts?.length) {
    return existingContext;
  }

  const primaryRestaurantId =
    normalizeRestaurantId(existingContext?.primary_restaurant_id) ||
    normalizeRestaurantId(user?.id);

  if (!primaryRestaurantId) {
    return existingContext || buildStandaloneContext(user);
  }

  try {
    const response = await fetch(`${API_BASE}/restaurant/account-links/list`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: primaryRestaurantId }),
    });

    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.success === false) {
      if (existingContext) return existingContext;
      return buildStandaloneContext(user);
    }

    const nextContext =
      json?.account_link_context ||
      json?.data?.account_link_context ||
      json?.data ||
      null;

    if (!nextContext) {
      if (existingContext) return existingContext;
      return buildStandaloneContext(user);
    }

    const persisted = applyAccountLinkContext(nextContext);
    return getAccountLinkContext(persisted);
  } catch {
    return existingContext || buildStandaloneContext(user);
  }
};
